import { useEffect } from 'react';
import { landingIntentFromReferrer, saveLandingIntent } from './landing-intent';
import { ensureMe } from './me';

/**
 * Catches a marketing landing page's `?referrer=<page>` off the URL and keeps the intent it names in local
 * storage, to be sent with the onboarding kickoff (see `use-chat.ts` → `startOnboarding`).
 *
 * Mounted from `_app`, so it runs on whichever route the visitor actually lands on: the home for a signed-in
 * user, or `/welcome` for one with no recipient yet — the onboarding gate forwards the `referrer` there
 * (see `onboarding-gate.js`), and the sign-in bounce keeps it in `postLogin`. Read from `window.location`
 * rather than the router, because `/welcome` replaces its own URL as soon as the recipient exists.
 *
 * An unrecognised referrer stores nothing, so a visitor from anywhere else is untouched.
 *
 * Bound to the signed-in user, stored FIRST unbound and upgraded when `/api/me` answers, the same order as
 * `use-capture-referrer.ts`: a slow identity call must not lose the intent.
 */
export function useCaptureLandingIntent(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const intent = landingIntentFromReferrer(new URLSearchParams(window.location.search).get('referrer'));
    if (!intent) return;
    const unbound = { intent, userId: '', capturedAt: Date.now() };
    saveLandingIntent(unbound);

    let cancelled = false;
    void ensureMe().then((me) => {
      if (cancelled || !me?.userId) return;
      saveLandingIntent({ ...unbound, userId: me.userId });
    });
    return () => {
      cancelled = true;
    };
  }, []);
}
